import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, Field, SelectField, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Details, Section, StateView, When, useAuth, useToast } from "@ozpanel/ui";

// Who actually answers for each domain, versus who we have been writing to.
//
// The server states the trap itself: "'Detected' is who answers for the domain on the internet
// right now. When it disagrees with the binding, every sync has been writing to a provider nobody
// reads." That is a failure with no symptom — records save, syncs report success, and the world
// sees none of it — which is exactly the class of thing this panel exists to make visible.

interface Binding {
  fqdn: string;
  mode?: string;
  provider?: string;
  credential?: string;
  auto_sync?: boolean;
  last_sync_at?: string;
  last_sync_result?: string;
  last_drift_at?: string;
  last_drift?: string;
}
interface DnsDomain {
  binding?: Binding;
  detected_provider?: string;
  live_nameservers?: string[] | null;
  mismatch?: string;
  /** How badly the binding and reality differ — decided by the server, which is the only place
   *  that knows which of the five cases this is. "" means they agree. */
  mismatch_severity?: "info" | "warn" | "critical" | "";
  /** Why "actually answers" is blank, when it is. The four reasons are materially different and
   *  one of them — a subdomain served by its parent zone — is the normal case. */
  detected_note?: string;
}

// GET /dns/providers and /dns/credentials (internal/modules/dnsmanage; docs/design/DNS-PROVIDERS.md).
interface CredentialField {
  key: string;
  label: string;
  help?: string;
  secret: boolean;
}
interface Provider {
  name: string;
  title: string;
  for_accounts: boolean;
  credential_fields?: CredentialField[] | null;
  dnssec?: boolean;
  // The provider's own setup guide (the token's permissions and steps) — the same text the welcome email carries.
  guide?: {
    permissions: { scope: string; resource: string; level: string; why: string; optional?: boolean }[];
    steps: string[];
    before?: string[] | null;
  };
}
interface CredentialRow {
  credential: {
    id: number;
    tenant_id: number;
    provider: string;
    label: string;
    account: string;
    zones: string[];
    verified_at?: string;
    last_error?: string;
  };
  ref: string;
  used_by?: string[] | null;
}

/** The note in a column's worth of words; the full sentence is the tooltip. */
function shortNote(note: string): string {
  if (note.startsWith("no delegation of its own")) return "served by its parent zone";
  if (note.startsWith("the nameserver lookup failed")) return "lookup failed";
  if (note.startsWith("this node has no resolver")) return "nobody was asked";
  if (note.startsWith("answered by nameservers")) return "unrecognised nameservers";
  if (note.startsWith("no nameservers are published")) return "nothing answers for it";
  return "unknown";
}

/** Sorts the worst first: a domain the world cannot resolve belongs above one that merely wants
 *  its binding tidied. */
function severityRank(sev?: string): number {
  switch (sev) {
    case "critical":
      return 3;
    case "warn":
      return 2;
    case "info":
      return 1;
    default:
      return 0;
  }
}

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

interface Editing {
  fqdn: string;
  mode: string;
  provider: string;
  credential: string;
  auto: boolean;
}

export function DnsProviderOps() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [editing, setEditing] = React.useState<Editing | null>(null);
  const [clearing, setClearing] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState<string | null>(null);
  const [backstop, setBackstop] = React.useState(false);

  // The per-domain read: what the registrar and the internet say about ONE name, which the list
  // summarises but cannot explain.
  const one = useQuery({
    queryKey: ["dns-domain", open],
    enabled: !!open,
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/dns/domains/${encodeURIComponent(String(open))}`),
    retry: false,
  });

  const q = useQuery({
    queryKey: ["dns-domains"],
    queryFn: () => client.apiFetch<{ items: DnsDomain[] | null; total?: number; note?: string }>("/dns/domains"),
    retry: false,
  });
  const providers = useQuery({
    queryKey: ["dns-providers"],
    queryFn: () => client.apiFetch<{ items: Provider[] | null }>("/dns/providers"),
    retry: false,
  });
  const creds = useQuery({
    queryKey: ["dns-credentials"],
    queryFn: () => client.apiFetch<{ items: CredentialRow[] | null }>("/dns/credentials"),
    retry: false,
  });
  const providerOf = (name?: string) => (providers.data?.items ?? []).find((p) => p.name === name);
  const credLabel = (ref?: string) => {
    const c = (creds.data?.items ?? []).find((r) => r.ref === ref)?.credential;
    return c ? c.label || c.account : ref;
  };

  const refresh = () => {
    for (const k of ["dns-domains", "dns-credentials", "dns-domain"]) void qc.invalidateQueries({ queryKey: [k] });
  };
  const run = (label: string, p: Promise<unknown>) =>
    p
      .then(() => {
        notify({ kind: "success", title: label });
        refresh();
      })
      .catch((e: unknown) => {
        notify({ kind: "error", title: `${label} failed`, body: errText(e) });
        refresh();
      });

  // A sync is a write with an explicit confirm; without it the server only answers the plan.
  const syncNow = (fqdn: string) =>
    void client
      .apiFetch<{ verified?: boolean; summary?: string }>(`/dns/domains/${encodeURIComponent(fqdn)}/sync`, {
        method: "POST",
        body: JSON.stringify({ confirm: true }),
      })
      .then((o) => notify({ kind: o.verified ? "success" : "error", title: o.verified ? `${fqdn} is in step` : `${fqdn}: sent, not confirmed`, body: o.summary }))
      .catch((e: unknown) => notify({ kind: "error", title: `${fqdn}: the sync failed`, body: errText(e) }))
      .finally(refresh);

  const cols: DataListColumn<DnsDomain>[] = [
    {
      key: "fqdn", label: "Domain", sortable: true, value: (d) => d.binding?.fqdn ?? "",
      render: (d) => <span className="font-mono text-[12.5px]">{d.binding?.fqdn}</span>,
    },
    {
      key: "provider", label: "We write to", sortable: true, value: (d) => d.binding?.provider ?? "",
      render: (d) =>
        d.binding?.provider ? (
          <span className="text-[12.5px]">
            {providerOf(d.binding.provider)?.title ?? d.binding.provider}
            {d.binding.credential && <span className="block text-[11px] text-muted">{credLabel(d.binding.credential)}</span>}
          </span>
        ) : (
          <span className="text-[12px] text-muted">{d.binding?.mode === "unmanaged" ? "not managed" : d.binding?.mode === "ours" ? "ours" : "—"}</span>
        ),
    },
    {
      key: "detected", label: "Actually answers", sortable: true, value: (d) => d.detected_provider ?? "",
      render: (d) =>
        d.detected_provider ? (
          <span className="text-[12.5px]">{d.detected_provider}</span>
        ) : (
          // "unknown" was printed for four different situations, one of which is a subdomain
          // working exactly as intended. The server now says which, so this can too.
          <span className="text-[12px] text-muted" title={d.detected_note}>
            {d.detected_note ? shortNote(d.detected_note) : "unknown"}
          </span>
        ),
    },
    {
      // Only two of the five states this can describe are faults; the server says which.
      key: "mismatch", label: "", value: (d) => severityRank(d.mismatch_severity),
      render: (d) => {
        if (!d.mismatch) return null;
        const sev = d.mismatch_severity || "warn";
        if (sev === "info") {
          return (
            <span title={d.mismatch} className="text-[11.5px] text-muted">
              served here — nothing to sync
            </span>
          );
        }
        return (
          <span title={d.mismatch}>
            <StatusPill tone={sev === "critical" ? "bad" : "warn"}>
              {sev === "critical" ? "the world sees a different zone" : "binding does not match reality"}
            </StatusPill>
          </span>
        );
      },
    },
    {
      key: "auto", label: "Auto-sync", value: (d) => (d.binding?.auto_sync ? 1 : 0),
      render: (d) => (d.binding?.auto_sync ? <StatusPill tone="ok">on</StatusPill> : <span className="text-[12px] text-muted">off</span>),
    },
    {
      // What the last push did (the watcher's, a person's, or the backstop's) and any drift the backstop put back.
      key: "last", label: "Last sync", sortable: true, value: (d) => d.binding?.last_sync_at ?? "",
      render: (d) => {
        const b = d.binding;
        if (!b?.last_sync_result) return <span className="text-[12px] text-muted">—</span>;
        const failed = b.last_sync_result.startsWith("failed");
        return (
          <span className="block max-w-[22rem] text-[12px]" title={b.last_sync_result}>
            <StatusPill tone={failed ? "bad" : "ok"}>{failed ? "failed" : "ok"}</StatusPill>{" "}
            {b.last_sync_at && <When value={b.last_sync_at} />}
            <span className="line-clamp-2 text-muted">{b.last_sync_result.replace(/^(ok|failed): /, "")}</span>
            {b.last_drift && <span className="line-clamp-2 text-warn">{b.last_drift}</span>}
          </span>
        );
      },
    },
  ];

  const ed = editing;
  const edProvider = providerOf(ed?.provider);
  const edCreds = (creds.data?.items ?? []).filter((r) => r.credential.provider === ed?.provider);

  return (
    <div className="space-y-4">
      <Section
        title="DNS hosting"
        hint={q.data?.total != null ? `${q.data.total}` : undefined}
        bodyClassName="p-0"
        actions={
          <Button size="sm" variant="ghost" onClick={() => setBackstop(true)}>
            Compare all now
          </Button>
        }
      >
        {q.data?.note && <div className="border-b border-line px-4 py-2.5 text-[12px] leading-relaxed text-muted">{q.data.note}</div>}
        <div className="p-4">
          <DataList
            items={q.data?.items ?? []}
            columns={cols}
            getRowKey={(d) => String(d.binding?.fqdn)}
            loading={q.isLoading}
            error={q.error}
            errorContext="the DNS hosting bindings"
            emptyMessage="No domains."
            rowActions={(d) => (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost"
                  onClick={() => setOpen(open === d.binding?.fqdn ? null : String(d.binding?.fqdn))}>
                  {open === d.binding?.fqdn ? "Close" : "Check"}
                </Button>
                <Button size="sm" variant="ghost"
                  onClick={() =>
                    setEditing({
                      fqdn: d.binding?.fqdn ?? "",
                      mode: d.binding?.mode && d.binding.mode !== "unmanaged" ? d.binding.mode : "external",
                      provider: d.binding?.provider ?? "",
                      credential: d.binding?.credential ?? "",
                      auto: !!d.binding?.auto_sync,
                    })
                  }>
                  Provider
                </Button>
                {d.binding?.provider && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => syncNow(String(d.binding?.fqdn))}>
                      Sync
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => setClearing(d.binding?.fqdn ?? "")}>Unbind</Button>
                  </>
                )}
              </div>
            )}
          />
        </div>
      </Section>

      {open && (
        <Section title={`Delegation — ${open}`}
          actions={<Button size="sm" variant="ghost" onClick={() => setOpen(null)}>Close</Button>}>
          <StateView loading={one.isLoading} error={one.error} errorContext="this domain">
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-lg border border-line bg-bg px-3 py-2 font-mono text-[11px]">
              {JSON.stringify(one.data, null, 1)}
            </pre>
          </StateView>
        </Section>
      )}

      <ProviderTokens providers={providers.data?.items ?? []} rows={creds.data?.items ?? []} loading={creds.isLoading} error={creds.error} onChanged={refresh} />

      <ConfirmDialog
        open={!!ed}
        onOpenChange={(o) => !o && setEditing(null)}
        title={`Where is ${ed?.fqdn ?? ""}'s DNS hosted?`}
        confirmLabel="Save"
        destructive={false}
        confirmDisabled={!!ed && ed.mode === "external" && (!ed.provider || (!!edProvider?.credential_fields?.length && !ed.credential))}
        body={
          ed && (
            <div className="space-y-2">
              <SelectField
                label="Hosted by"
                value={ed.mode}
                onChange={(mode) => setEditing({ ...ed, mode })}
                options={[
                  { value: "ours", label: "our nameservers" },
                  { value: "external", label: "an external provider" },
                  { value: "unmanaged", label: "not decided" },
                ]}
              />
              {ed.mode === "external" && (
                <>
                  <SelectField
                    label="Provider"
                    value={ed.provider}
                    onChange={(provider) => {
                      const p = providerOf(provider);
                      // A provider that marks its own records mirrors safely, so it follows our zone by default.
                      setEditing({ ...ed, provider, credential: "", auto: !!p?.credential_fields?.length || ed.auto });
                    }}
                    options={[
                      { value: "", label: "choose…" },
                      ...(providers.data?.items ?? [])
                        .filter((p) => p.name !== "self")
                        .map((p) => ({ value: p.name, label: p.title })),
                    ]}
                  />
                  {!!edProvider?.credential_fields?.length && (
                    <SelectField
                      label="Credential"
                      value={ed.credential}
                      onChange={(credential) => setEditing({ ...ed, credential })}
                      help={edCreds.length === 0 ? "No credential for this provider is stored yet — add one under Provider credentials." : undefined}
                      options={[
                        { value: "", label: "choose…" },
                        ...edCreds.map((r) => ({
                          value: r.ref,
                          label: `${r.credential.label || r.credential.account} — ${r.credential.tenant_id === 0 ? "platform" : `account ${r.credential.tenant_id}`}`,
                          help: r.credential.zones.length ? `Reaches ${r.credential.zones.join(", ")}` : "Reaches no zone",
                        })),
                      ]}
                    />
                  )}
                  <label className="flex items-center gap-2 text-[13px]">
                    <input type="checkbox" checked={ed.auto} onChange={(e) => setEditing({ ...ed, auto: e.target.checked })} />
                    Keep the provider in step with our zone automatically
                  </label>
                </>
              )}
              <p className="text-[11.5px] leading-relaxed text-muted">
                This must be who ACTUALLY answers for the domain. Binding it to a provider that does not means every
                sync writes records nobody on the internet reads. An external binding is checked with the provider
                and, when it follows our zone, synced at once.
              </p>
            </div>
          )
        }
        onConfirm={() => {
          const e = editing;
          setEditing(null);
          if (!e?.fqdn) return;
          const body: Record<string, unknown> = { mode: e.mode };
          if (e.mode === "external") Object.assign(body, { provider: e.provider, credential: e.credential, auto_sync: e.auto });
          void client
            .apiFetch<{ sync?: { summary?: string }; sync_error?: string }>(`/dns/domains/${encodeURIComponent(e.fqdn)}/provider`, {
              method: "PUT",
              body: JSON.stringify(body),
            })
            .then((out) => {
              if (out.sync_error) notify({ kind: "error", title: "Saved, but the first sync failed", body: out.sync_error });
              else notify({ kind: "success", title: "Provider saved", body: out.sync?.summary });
            })
            .catch((err: unknown) => notify({ kind: "error", title: "The provider was not saved", body: errText(err) }))
            .finally(refresh);
        }}
      />

      <ConfirmDialog
        open={!!clearing}
        onOpenChange={(o) => !o && setClearing(null)}
        title={`Stop managing DNS for ${clearing ?? ""}?`}
        confirmLabel="Unbind"
        destructive
        body={<p>Records already at the provider are left exactly as they are. This only stops us writing to it.</p>}
        onConfirm={() => {
          const f = clearing;
          setClearing(null);
          if (f) void run("Unbound", client.apiFetch(`/dns/domains/${encodeURIComponent(f)}/provider`, { method: "DELETE" }));
        }}
      />

      <ConfirmDialog
        open={backstop}
        onOpenChange={setBackstop}
        title="Compare every auto-synced zone with its provider now?"
        confirmLabel="Compare"
        destructive={false}
        body={
          <p>
            The same pass that runs every 15 minutes on the master: each zone is compared with its provider and what was
            changed there is put back; each credential in use is checked.
          </p>
        }
        onConfirm={() => {
          setBackstop(false);
          void client
            .apiFetch<{ summary?: string; error?: string }>("/dns/hosting/sync-all", { method: "POST" })
            .then((o) => notify({ kind: o.error ? "error" : "success", title: o.summary ?? "Compared", body: o.error }))
            .catch((e: unknown) => notify({ kind: "error", title: "The compare failed", body: errText(e) }))
            .finally(refresh);
        }}
      />
    </div>
  );
}

/** Stored provider credentials — every account's and the platform's own; never their values. */
function ProviderTokens({
  providers,
  rows,
  loading,
  error,
  onChanged,
}: {
  providers: Provider[];
  rows: CredentialRow[];
  loading: boolean;
  error: unknown;
  onChanged: () => void;
}) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [removing, setRemoving] = React.useState<CredentialRow | null>(null);
  const [adding, setAdding] = React.useState(false);
  const takes = providers.filter((p) => (p.credential_fields ?? []).length > 0);
  const [form, setForm] = React.useState<{ provider: string; label: string; tenant: string; values: Record<string, string> }>({
    provider: "", label: "", tenant: "0", values: {},
  });
  const fields = takes.find((p) => p.name === form.provider)?.credential_fields ?? [];

  // The check also probes, per zone, every permission of the provider's guide (read-only) — a missing one is
  // named here so it can be added at the provider.
  type PermReport = { zone?: string; error?: string; results?: { scope: string; resource: string; level: string; state: string }[] | null; settings?: Record<string, string> | null };
  const check = (r: CredentialRow) =>
    void client
      .apiFetch<{ ok?: boolean; error?: string; check?: { zones?: { name: string }[] | null }; permissions?: PermReport[] | null }>(
        `/dns/credentials/${r.credential.id}/check`,
        { method: "POST" },
      )
      .then((o) => {
        const lines: string[] = [];
        for (const rep of o.permissions ?? []) {
          if (rep.error) {
            lines.push(`${rep.zone ?? "?"}: could not check — ${rep.error}`);
            continue;
          }
          const miss = (rep.results ?? []).filter((x) => x.state !== "granted");
          lines.push(
            `${rep.zone}: ` +
              (miss.length ? miss.map((x) => `${x.scope} · ${x.resource} · ${x.level} ${x.state}`).join("; ") : "every permission present") +
              (rep.settings?.ssl_mode ? ` · SSL/TLS ${rep.settings.ssl_mode}` : ""),
          );
        }
        const anyMissing = (o.permissions ?? []).some((rep) => (rep.results ?? []).some((x) => x.state === "missing"));
        notify({
          kind: o.ok && !anyMissing ? "success" : "error",
          title: !o.ok ? "The provider refused it" : anyMissing ? "Accepted, but permissions are missing" : "The provider accepts it",
          body: o.ok
            ? [`Reaches ${(o.check?.zones ?? []).map((z) => z.name).join(", ") || "no zone"}`, ...lines].join("\n")
            : o.error,
        });
      })
      .catch((e: unknown) => notify({ kind: "error", title: "The check failed", body: errText(e) }))
      .finally(onChanged);

  const cols: DataListColumn<CredentialRow>[] = [
    { key: "label", label: "Credential", sortable: true, value: (r) => r.credential.label, render: (r) => (
      <span className="text-[12.5px]">
        {r.credential.label || r.credential.account}
        <span className="block text-[11px] text-muted">{providers.find((p) => p.name === r.credential.provider)?.title ?? r.credential.provider} · {r.credential.account}</span>
      </span>
    ) },
    { key: "owner", label: "Owner", sortable: true, value: (r) => r.credential.tenant_id, render: (r) => (
      <span className="text-[12px]">{r.credential.tenant_id === 0 ? "platform" : `account ${r.credential.tenant_id}`}</span>
    ) },
    { key: "zones", label: "Reaches", value: (r) => r.credential.zones.join(" "), render: (r) => (
      <span className="font-mono text-[11.5px]">{r.credential.zones.join(", ") || "—"}</span>
    ) },
    { key: "used", label: "Used by", value: (r) => (r.used_by ?? []).length, render: (r) => (
      <span className="font-mono text-[11.5px]">{(r.used_by ?? []).join(", ") || "—"}</span>
    ) },
    { key: "checked", label: "Last check", sortable: true, value: (r) => r.credential.verified_at ?? "", render: (r) => (
      <span className="text-[12px]">
        {r.credential.last_error ? <StatusPill tone="bad">refused</StatusPill> : r.credential.verified_at ? <When value={r.credential.verified_at} /> : "—"}
        {r.credential.last_error && <span className="line-clamp-2 text-muted" title={r.credential.last_error}>{r.credential.last_error}</span>}
      </span>
    ) },
  ];

  return (
    <Section
      title="Provider credentials"
      hint={`${rows.length}`}
      bodyClassName="p-0"
      actions={takes.length > 0 ? <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>Add credential</Button> : undefined}
    >
      <div className="p-4">
        <DataList
          items={rows}
          columns={cols}
          getRowKey={(r) => r.ref}
          loading={loading}
          error={error}
          errorContext="the stored credentials"
          emptyMessage="No credential stored. Accounts add their own Cloudflare token when they connect a domain."
          rowActions={(r) => (
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => check(r)}>Check</Button>
              <Button size="sm" variant="danger" disabled={(r.used_by ?? []).length > 0}
                title={(r.used_by ?? []).length > 0 ? "In use — move those domains first" : undefined}
                onClick={() => setRemoving(r)}>
                Delete
              </Button>
            </div>
          )}
        />
      </div>
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Delete the credential “${removing?.credential.label || removing?.credential.account || ""}”?`}
        confirmLabel="Delete"
        destructive
        body={<p>It is deleted here only; the token still exists at the provider until it is revoked there.</p>}
        onConfirm={() => {
          const r = removing;
          setRemoving(null);
          if (!r) return;
          void client
            .apiFetch(`/dns/credentials/${r.credential.id}`, { method: "DELETE" })
            .then(() => notify({ kind: "success", title: "Credential deleted" }))
            .catch((e: unknown) => notify({ kind: "error", title: "Not deleted", body: errText(e) }))
            .finally(onChanged);
        }}
      />
      <ConfirmDialog
        open={adding}
        onOpenChange={setAdding}
        title="Store a provider credential"
        confirmLabel="Check and store"
        destructive={false}
        confirmDisabled={!form.provider || fields.some((f) => !(form.values[f.key] ?? "").trim())}
        body={
          <div className="space-y-2">
            <SelectField
              label="Provider"
              value={form.provider}
              onChange={(provider) => setForm({ ...form, provider, values: {} })}
              options={[{ value: "", label: "choose…" }, ...takes.map((p) => ({ value: p.name, label: p.title }))]}
            />
            {fields.map((f) => (
              <Field key={f.key} mono={f.secret} type={f.secret ? "password" : "text"} autoComplete="off" label={f.label} help={f.help}
                value={form.values[f.key] ?? ""} onChange={(v) => setForm({ ...form, values: { ...form.values, [f.key]: v } })} />
            ))}
            {(() => {
              const g = takes.find((p) => p.name === form.provider)?.guide;
              if (!g) return null;
              return (
                <Details summary="Permissions the token needs, and how the customer makes it">
                  <ul className="space-y-0.5">
                    {g.permissions.map((p) => (
                      <li key={p.resource}>
                        <span className="font-mono">
                          {p.scope} · {p.resource} · {p.level}
                        </span>{" "}
                        — {p.why}
                        {p.optional ? " (recommended)" : ""}
                      </li>
                    ))}
                  </ul>
                  <ol className="mt-2 list-decimal space-y-1 pl-5">
                    {g.steps.map((st) => (
                      <li key={st}>{st}</li>
                    ))}
                  </ol>
                  {(g.before ?? []).length > 0 && (
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {(g.before ?? []).map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  )}
                </Details>
              );
            })()}
            <Field label="Name for it" value={form.label} onChange={(label) => setForm({ ...form, label })} />
            <Field label="Owner account id (0 = the platform's own)" value={form.tenant} onChange={(tenant) => setForm({ ...form, tenant })} mono />
          </div>
        }
        onConfirm={() => {
          const f = form;
          setAdding(false);
          void client
            .apiFetch("/dns/credentials", {
              method: "POST",
              body: JSON.stringify({ provider: f.provider, label: f.label.trim(), values: f.values, tenant_id: Number(f.tenant) || 0 }),
            })
            .then(() => {
              notify({ kind: "success", title: "Credential checked and stored" });
              setForm({ provider: "", label: "", tenant: "0", values: {} });
            })
            .catch((e: unknown) => notify({ kind: "error", title: "Not stored", body: errText(e) }))
            .finally(onChanged);
        }}
      />
    </Section>
  );
}

// ───────────────────────────── namecheap ─────────────────────────────

interface NcStatus {
  enabled?: boolean;
  reachable?: boolean;
  sandbox?: boolean;
  api_user?: string;
  api_key_present?: boolean;
  client_ip_sent?: string;
  endpoint?: string;
  nameservers?: string[] | null;
  detail?: string;
}

export function NamecheapOps() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [open, setOpen] = React.useState<string | null>(null);
  const [syncing, setSyncing] = React.useState<string | null>(null);

  const status = useQuery({
    queryKey: ["nc-status"],
    queryFn: () => client.apiFetch<NcStatus>("/namecheap/status"),
    retry: false,
  });
  const domains = useQuery({
    queryKey: ["nc-domains"],
    queryFn: () => client.apiFetch<{ domains?: { name?: string; expires?: string }[] | null; items?: { name?: string }[] | null }>("/namecheap/domains"),
    retry: false,
    enabled: status.data?.enabled === true,
  });
  const hosts = useQuery({
    queryKey: ["nc-hosts", open],
    enabled: !!open,
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/namecheap/domains/${encodeURIComponent(String(open))}/hosts`),
    retry: false,
  });
  const delegation = useQuery({
    queryKey: ["nc-delegation", open],
    enabled: !!open,
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/namecheap/domains/${encodeURIComponent(String(open))}/delegation`),
    retry: false,
  });
  const ds = useQuery({
    queryKey: ["nc-ds", open],
    enabled: !!open,
    queryFn: () => client.apiFetch<Record<string, unknown>>(`/namecheap/domains/${encodeURIComponent(String(open))}/ds`),
    retry: false,
  });

  const run = (label: string, p: Promise<unknown>) =>
    p
      .then(() => {
        notify({ kind: "success", title: label });
        for (const k of ["nc-domains", "nc-hosts", "nc-delegation"]) void qc.invalidateQueries({ queryKey: [k] });
      })
      .catch((e: unknown) => notify({ kind: "error", title: `${label} failed`, body: e instanceof Error ? e.message : String(e) }));

  const st = status.data;

  return (
    <div className="space-y-6">
      <Section title="Namecheap API">
        <StateView loading={status.isLoading} error={status.error} errorContext="the Namecheap integration">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={st?.enabled ? "ok" : "neutral"}>{st?.enabled ? "enabled" : "disabled"}</StatusPill>
            {st?.enabled && <StatusPill tone={st?.reachable ? "ok" : "bad"}>{st?.reachable ? "reachable" : "unreachable"}</StatusPill>}
            {st?.sandbox && <StatusPill tone="warn">sandbox</StatusPill>}
            {st?.api_key_present === false && <StatusPill tone="bad">no API key</StatusPill>}
          </div>
          <div className="mt-3 grid gap-x-8 gap-y-1 text-[12.5px] sm:grid-cols-2">
            <R label="API user" value={st?.api_user || "—"} />
            <R label="Endpoint" value={st?.endpoint || "—"} />
            {/* Namecheap allowlists by SOURCE IP, so the address we present is the single most
                common reason a correct key is refused. */}
            <R label="We appear as" value={st?.client_ip_sent || "—"} />
            <R label="Their nameservers" value={(st?.nameservers ?? []).join(", ") || "—"} />
          </div>
          {st?.detail && <p className="mt-2 max-w-3xl text-[12px] leading-relaxed text-muted">{st.detail}</p>}
          {!st?.enabled && (
            <p className="mt-2 max-w-3xl text-[12px] leading-relaxed text-muted">
              Turn it on and set the API credentials under this module&apos;s Settings. Namecheap
              also requires the address above to be allowlisted in your account.
            </p>
          )}
        </StateView>
      </Section>

      {st?.enabled && (
        <Section title="Domains at the registrar" bodyClassName="p-0">
          <div className="p-4">
            <StateView loading={domains.isLoading} error={domains.error} errorContext="the registrar's domain list">
              <ul className="space-y-1">
                {(domains.data?.domains ?? domains.data?.items ?? []).map((d) => (
                  <li key={String(d.name)} className="flex flex-wrap items-center justify-between gap-2 border-b border-line py-1.5">
                    <span className="font-mono text-[12.5px]">{d.name}</span>
                    <span className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setOpen(open === d.name ? null : String(d.name))}>
                        {open === d.name ? "Close" : "Inspect"}
                      </Button>
                      {/* A GET is a PLAN (handleSync writes only on POST ?apply=true): this button
                          used to send the GET and say "Synced" while nothing reached the registrar. */}
                      <Button size="sm" onClick={() => setSyncing(syncing === d.name ? null : String(d.name))}>
                        {syncing === d.name ? "Close sync" : "Sync…"}
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => void run("Nameservers pointed here", client.apiFetch(`/namecheap/domains/${encodeURIComponent(String(d.name))}/ns`, { method: "POST" }))}>
                        Point at us
                      </Button>
                    </span>
                  </li>
                ))}
                {(domains.data?.domains ?? domains.data?.items ?? []).length === 0 && (
                  <li className="text-[13px] text-muted">No domains in this account.</li>
                )}
              </ul>
            </StateView>
          </div>
        </Section>
      )}

      {open && (
        <Section title={open} actions={<Button size="sm" variant="ghost" onClick={() => setOpen(null)}>Close</Button>}>
          <div className="grid gap-4 lg:grid-cols-3">
            {([["Delegation", delegation], ["Host records", hosts], ["DNSSEC (DS)", ds]] as const).map(([label, qq]) => (
              <div key={label}>
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted">{label}</div>
                <StateView loading={qq.isLoading} error={qq.error} errorContext={label.toLowerCase()}>
                  <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded border border-line bg-bg p-2 font-mono text-[11px]">
                    {JSON.stringify(qq.data, null, 1)}
                  </pre>
                </StateView>
              </div>
            ))}
          </div>
        </Section>
      )}

      {syncing && <NamecheapSync domain={syncing} onClose={() => setSyncing(null)} onApplied={() => {
        for (const k of ["nc-domains", "nc-hosts", "nc-delegation"]) void qc.invalidateQueries({ queryKey: [k] });
      }} />}
    </div>
  );
}

// GET/POST /namecheap/domains/{domain}/sync (namecheap/sync.go handleSync). setHosts has no partial
// update — what is sent becomes the registrar's whole zone — so the plan is shown FIRST (a GET, which
// writes nothing), with what disappears, and the write is a POST ?apply=true after a confirmation that
// repeats it. The answer's `verified` says whether the zone read back as sent: Namecheap accepts records
// it does not store, so "applied" alone is not the fact.
interface NcRecord { name: string; type: string; value: string; ttl: number; mx_pref?: number }
interface NcSyncAnswer {
  dry_run: boolean;
  applied?: boolean;
  summary?: string;
  note?: string;
  using_namecheap_dns?: boolean;
  verified?: boolean;
  verify_error?: string;
  plan?: { mode: string; added?: NcRecord[] | null; removed?: NcRecord[] | null; kept: number; dropped?: NcRecord[] | null };
}
const rec = (r: NcRecord) => `${r.name} ${r.type}${r.mx_pref ? ` ${r.mx_pref}` : ""} ${r.value}`;

function NamecheapSync({ domain, onClose, onApplied }: { domain: string; onClose: () => void; onApplied: () => void }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const [mode, setMode] = React.useState<"merge" | "mirror">("merge");
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState<NcSyncAnswer | null>(null);
  const plan = useQuery({
    queryKey: ["nc-sync-plan", domain, mode],
    queryFn: () => client.apiFetch<NcSyncAnswer>(`/namecheap/domains/${encodeURIComponent(domain)}/sync?mode=${mode}`),
    retry: false,
  });
  const p = plan.data?.plan;
  const added = p?.added ?? [];
  const removed = p?.removed ?? [];
  const dropped = p?.dropped ?? [];
  const noop = !!p && added.length === 0 && removed.length === 0;
  const blocked = (plan.data?.note ?? "").startsWith("BLOCKED");

  const apply = () => {
    setBusy(true);
    void client
      .apiFetch<NcSyncAnswer>(`/namecheap/domains/${encodeURIComponent(domain)}/sync?mode=${mode}&apply=true`, { method: "POST" })
      .then((a) => {
        setResult(a);
        notify({
          kind: a.applied && a.verified === false ? "error" : "success",
          title: a.applied ? (a.verified === false ? `${domain}: sent, not all of it stored` : `${domain}: registrar updated`) : `${domain}: nothing sent`,
          body: a.summary,
        });
        onApplied();
        void plan.refetch();
      })
      .catch((e: unknown) => notify({ kind: "error", title: `${domain}: sync failed`, body: e instanceof Error ? e.message : String(e) }))
      .finally(() => {
        setBusy(false);
        setConfirming(false);
      });
  };

  const list = (title: string, rows: NcRecord[], tone: string) =>
    rows.length > 0 && (
      <div>
        <div className={`mb-1 text-[11px] font-medium uppercase tracking-wide ${tone}`}>{title} ({rows.length})</div>
        <ul className="space-y-0.5 font-mono text-[11.5px]">
          {rows.map((r) => (
            <li key={rec(r)} className="break-all">{rec(r)}</li>
          ))}
        </ul>
      </div>
    );

  return (
    <Section
      title={`Sync ${domain} to the registrar`}
      actions={<Button size="sm" variant="ghost" onClick={onClose}>Close</Button>}
    >
      <div className="space-y-3 text-[12.5px]">
        <SelectField
          label="Mode"
          value={mode}
          onChange={(v) => setMode(v === "mirror" ? "mirror" : "merge")}
          options={[
            { value: "merge", label: "merge — keep what is already at the registrar, add ours", help: "Records added at the registrar by hand survive." },
            { value: "mirror", label: "mirror — make the registrar exactly our zone", help: "Anything at the registrar that is not in our zone is DELETED. For a clean cutover." },
          ]}
        />
        <StateView loading={plan.isLoading} error={plan.error} errorContext="the sync plan">
          {p && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill tone={noop ? "ok" : removed.length > 0 ? "warn" : "neutral"}>{plan.data?.summary ?? "planned"}</StatusPill>
                {plan.data?.using_namecheap_dns === false && <StatusPill tone="warn">delegated away — not served</StatusPill>}
              </div>
              {plan.data?.note && <p className={`max-w-3xl leading-relaxed ${blocked ? "text-crit" : "text-muted"}`}>{plan.data.note}</p>}
              <div className="grid gap-4 lg:grid-cols-3">
                {list("Added", added, "text-ok")}
                {list("Removed", removed, "text-crit")}
                {list("Cannot be stored by the registrar", dropped, "text-warn")}
              </div>
              <div className="flex justify-end">
                <Button size="sm" variant={removed.length > 0 ? "danger" : "accent"} disabled={noop || blocked || busy} onClick={() => setConfirming(true)}
                  title={noop ? "The registrar already matches the zone" : blocked ? "The server refuses this write — see the note" : undefined}>
                  Apply to the registrar
                </Button>
              </div>
            </div>
          )}
        </StateView>
        {result && (
          <div className="rounded border border-line bg-bg2 px-3 py-2 text-[12px] leading-relaxed">
            <div>{result.summary}</div>
            {result.applied && (
              <div className={result.verified ? "text-ok" : "text-warn"}>
                {result.verified ? "Read back from the registrar: every record is there." : result.verify_error ? `Not confirmed: ${result.verify_error}` : "The registrar did not keep every record sent."}
              </div>
            )}
            {result.note && <div className="text-muted">{result.note}</div>}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={(o) => !o && setConfirming(false)}
        title={`Replace ${domain}'s host records at the registrar?`}
        destructive={removed.length > 0}
        confirmLabel="Apply"
        pending={busy}
        onConfirm={apply}
        body={
          <div className="space-y-2 text-[12.5px] leading-relaxed">
            <p>
              The registrar's API has no partial update: the set sent becomes the domain's whole zone there ({mode}). {plan.data?.summary}.
            </p>
            {removed.length > 0 && <p className="text-crit">{removed.length} record(s) at the registrar are DELETED by this write.</p>}
          </div>
        }
      />
    </Section>
  );
}

function R({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-line py-1">
      <span className="text-muted">{label}</span>
      <span className="text-right font-mono text-[12px]">{value}</span>
    </div>
  );
}
